import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import CustomSelect from "../form/CustomSelect";
import CustomTooltip from "../CustomTooltip";
import Amount from "../Amount";
import { DeleteIcon } from "../../assets/icons/Icons";
import { CentsToDollars } from "../../utils/formatPrice";
import { lineTotalCents, lineTaxCents } from "./specialtyItemTotals";

const SpecialtyItemRow = ({ rowIndex, rowData, onFieldChange, removeRow, ...props }) => {
  const { t } = useTranslation();
  const { catalog = [], readOnly = false, loading = false } = props;

  const lineTotal = Number(CentsToDollars(lineTotalCents(rowData)));

  // A sold line recalled later carries the tax it was actually charged; a line
  // still being ordered has none yet, so it is worked out from the catalog's
  // taxable flag and the office rate, the same figure that feeds Tax: Items.
  const lineTax = Number(
    CentsToDollars(rowData.tax ?? lineTaxCents(rowData, props.taxRate))
  );

  // While ordering a line carries `taxable` from the catalog; a sold line
  // recalled later carries a `tax_rate` instead, NULL when it was not taxed.
  const taxable = rowData.taxable ?? rowData.tax_rate != null;

  const taxCell = <Amount amount={lineTax} />;

  // Zero tax on its own does not say whether an item is exempt or simply taxed
  // at nothing, so the name carries the reason.
  const itemName = taxable
    ? rowData.item_name
    : `${rowData.item_name} (${t("Non-taxable")})`;

  if (readOnly) {
    return (
      <>
        <td>{itemName}</td>
        <td><Amount amount={Number(CentsToDollars(rowData.unit_price))} /></td>
        <td>{rowData.quantity}</td>
        <td><Amount amount={lineTotal} /></td>
        <td>{taxCell}</td>
      </>
    );
  }

  const options = catalog.map((item) => ({
    value: item.id,
    label: item.taxable ? item.name : `${item.name} (${t("Non-taxable")})`,
  }));

  // Picking an item snapshots its price and taxability onto the row, so the line
  // keeps what it was worth when it was added.
  const selectItem = (option) => {
    const item = catalog.find((candidate) => candidate.id === option?.value);
    if (!item) return;

    onFieldChange({
      specialty_item: option,
      specialty_item_id: item.id,
      item_name: item.name,
      unit_price: item.unit_price,
      taxable: Boolean(item.taxable),
    });
  };

  return (
    <>
      <td>
        {/* Nothing is claimed about the catalog until it has actually arrived:
            an empty one and one still in flight are not the same thing. */}
        <CustomSelect
          placeholder={
            <div>
              {loading
                ? t("Loading...")
                : catalog.length
                ? t("Select item")
                : t("No specialty items set up yet")}
            </div>
          }
          options={options}
          value={rowData.specialty_item || null}
          onChange={selectItem}
          isLoading={loading}
          isDisabled={loading || !catalog.length}
        />
      </td>
      <td>
        {/* Priced by the catalog, so it is shown locked the way Card Type is. */}
        <input
          type="text"
          placeholder={t("Amount")}
          value={rowData.unit_price === undefined ? "" : CentsToDollars(rowData.unit_price)}
          disabled
          readOnly
        />
      </td>
      <td>
        <input
          type="number"
          min="1"
          max="99"
          placeholder={t("Quantity")}
          value={rowData.quantity || ""}
          onChange={(e) => {
            const quantity = e.target.value;
            if (quantity === "") {
              onFieldChange("quantity", "");
              return;
            }
            if (Number(quantity) >= 1 && Number(quantity) <= 99) {
              onFieldChange("quantity", Number(quantity));
            }
          }}
          onWheel={(e) => e.target.blur()}
          onKeyDown={(e) => { if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault(); }}
        />
      </td>
      <td><Amount amount={lineTotal} /></td>
      <td>{taxCell}</td>
      <td className="text-center">
        <Link to="#" onClick={(e) => { e.preventDefault(); removeRow(rowIndex); }}>
          <CustomTooltip icon={<DeleteIcon />} message={t("Remove Row")} />
        </Link>
      </td>
    </>
  );
};

export default SpecialtyItemRow;
